import {
  AutocompleteChangeReason,
  Box,
  createFilterOptions,
  inputBaseClasses,
  inputClasses,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Autocomplete } from '@sbu/components.autocomplete';
import {
  KEYWORD_TAG_GROUP,
  TagSchema,
  TagSchemaValueOption,
  useTags,
} from '@sbu/hooks.tags';

const emptyTag = { key: '', value: '' };

const getTag = (key = '', value = '') => (key ? `${key}:${value}` : value);

const getValueDropdownLabel = (option: TagSchemaValueOption) =>
  option.label ? `${option.value} - ${option.label}` : `${option.value}`;

export const ANY_TAG_VALUE = {
  label: 'Any Value in Schema',
  value: '*',
};

export interface TagInputProps {
  autocompleteList?: string[];
  autoFocus?: boolean;
  className?: string;
  contentTypes?: string[];
  disabled?: boolean;
  keyInputError?: boolean;
  keyInputErrorHint?: string;
  valueInputError?: boolean;
  valueInputErrorHint?: string;
  fullWidth?: boolean;
  loading?: boolean;
  placeholder?: string;
  showAnyValueSchemaOption?: boolean;
  tagSchemas?: TagSchema[];
  value?: string;
  onBlur?: () => void;
  onInputChange?: (value: string, isKeyInput: boolean) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onSetValue: (value: string) => void;
}

export const TagInput = ({
  autocompleteList,
  autoFocus,
  contentTypes,
  disabled,
  keyInputError,
  keyInputErrorHint,
  valueInputError,
  valueInputErrorHint,
  fullWidth = false,
  loading,
  onBlur,
  onInputChange,
  onKeyDown,
  onSetValue,
  placeholder,
  showAnyValueSchemaOption = false,
  tagSchemas,
  value,
}: TagInputProps) => {
  const tagInputRef = useRef<HTMLDivElement>(null);
  const keyInputRef = useRef<HTMLInputElement>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);

  const {
    deserializeTag,
    getTagSchema,
    getTagValueOptions,
    isPredefinedTagKey,
    isValidTagValue,
    tagKeyOptions,
  } = useTags(tagSchemas, contentTypes, autocompleteList);

  const tag = value ? deserializeTag(value) : emptyTag;
  const [tagKey, setTagKey] = useState(tag.key);
  const [tagValue, setTagValue] = useState(tag.value);
  const [valueEntered, setValueEntered] = useState(false);
  const [valueInputVisible, setValueInputVisible] = useState(false);
  const [tagValueOptions, setTagValueOptions] = useState<
    TagSchemaValueOption[]
  >([]);

  // These are used to track the current state of the input search query
  const [tagKeyInput, setTagKeyInput] = useState('');
  const [tagValueInput, setTagValueInput] = useState('');

  useEffect(() => {
    if (autoFocus) {
      keyInputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (valueInputVisible) {
      valueInputRef.current?.focus();
    }
  }, [valueInputVisible]);

  const clearInput = () => {
    setValueEntered(false);
    setValueInputVisible(false);
    setTagValue('');
    setTagKey('');
    setTagKeyInput('');
    setTagValueInput('');
  };

  const isValueFreeform = useMemo(
    () => !isPredefinedTagKey(tagKey) || !!getTagSchema(tagKey)?.freeform,
    [tagKey, isPredefinedTagKey, getTagSchema]
  );

  const onValueInputBlur = () => {
    setTagValue('');
    setTagValueInput('');
    setValueInputVisible(false);
    onBlur?.();
  };

  const onKeyInputChange = useCallback(
    (value: string) => {
      setTagKeyInput(value);
      if (onInputChange) {
        onInputChange(value, true);
      }
    },
    [onInputChange]
  );

  const onValueInputChange = useCallback(
    (value: string) => {
      setTagValueInput(value);
      if (isValueFreeform) {
        setValueEntered(
          !!value && isValidTagValue(tagKey, value) && !valueInputError
        );
      }

      if (onInputChange) {
        onInputChange(getTag(tagKey, value), false);
      }
    },
    [isValidTagValue, isValueFreeform, onInputChange, tagKey, valueInputError]
  );

  const onKeyInputFocus = () => {
    if (!!tagValue || valueInputVisible) {
      setTagValue('');
      setTagValueInput('');
      setValueInputVisible(false);
    }
  };

  const onKeyInputKeydown = (event: React.KeyboardEvent) => {
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const onSetValueOrAllowSubmit = (value: string) => {
    onSetValue(value);
    clearInput();
    onBlur?.();
  };

  const onValueChange = (
    value: { value: string } | null,
    isKeyInput: boolean,
    reason: AutocompleteChangeReason
  ) => {
    if ((!keyInputError && !valueInputError) || reason === 'selectOption') {
      setValueEntered(false);
      const trimmed: string = value?.value?.trim() || '';

      if (isKeyInput) {
        setTagKey(trimmed);

        if (!isValueFreeform) {
          setTagValue('');
          setTagValueInput('');
        }

        if (trimmed) {
          if (isPredefinedTagKey(trimmed)) {
            setValueEntered(false);
            setTagValueOptions(getTagValueOptions(trimmed));
            setValueInputVisible(true);
            onInputChange?.(trimmed, true);
          } else {
            onSetValueOrAllowSubmit(
              tagValue ? getTag(trimmed, tagValue) : trimmed
            );
          }
        }
      } else {
        setTagValue(trimmed);

        if (tagKey && trimmed) {
          onSetValueOrAllowSubmit(getTag(tagKey, trimmed));
        }
      }
    }
  };

  const selectedTagKeyOption = useMemo(() => {
    const tagKeyOption = tagKeyOptions.find((option) => option.key === tagKey);

    return tagKeyOption
      ? {
          label: tagKeyOption.label,
          value: tagKeyOption.key,
          group: tagKeyOption.group,
        }
      : {
          label: tagKey,
          value: tagKey,
          group: KEYWORD_TAG_GROUP,
        };
  }, [tagKey, tagKeyOptions]);

  const selectedTagValueOption = useMemo(() => {
    const tagValueOption = tagValueOptions.find(
      (option) => option.value === tagValue
    );

    return tagValueOption
      ? {
          label: getValueDropdownLabel(tagValueOption),
          value: tagValueOption.value,
        }
      : null;
  }, [tagValue, tagValueOptions]);

  const tagKeyFilter = createFilterOptions<{
    label: string;
    value: string;
    group: string;
  }>();
  const tagValueFilter = createFilterOptions<{
    label: string;
    value: string;
  }>();

  return (
    <Box
      alignItems='center'
      display='inline-flex'
      position='relative'
      ref={tagInputRef}
      sx={{
        overflowY: 'hidden',
      }}
      overflow='hidden'
      width={fullWidth ? '100%' : 400}
    >
      <Autocomplete
        autoHighlight
        blurOnSelect
        createable
        createNewOption={(value) => ({
          label: value,
          value,
          group: KEYWORD_TAG_GROUP,
        })}
        createNewOptionText={keyInputErrorHint || null}
        disableCreateOption={keyInputError}
        filterOptions={(options, params) => {
          const filtered = tagKeyFilter(options, params);

          const isExisting = options.some(
            ({ label, value }) => value === tagKeyInput || label === tagKeyInput
          );

          if (tagKeyInput !== '' && !isExisting && !keyInputError) {
            filtered.push({
              label: tagKeyInput,
              value: tagKeyInput,
              group: KEYWORD_TAG_GROUP,
            });
          }

          if (keyInputErrorHint) {
            filtered.push({
              label: keyInputErrorHint,
              value: 'tagKeyError',
              group: KEYWORD_TAG_GROUP,
            });
          }

          return filtered;
        }}
        fullWidth={fullWidth}
        groupBy={(option) => option.group}
        loading={loading}
        options={tagKeyOptions.map((option) => ({
          label: option.label,
          value: option.key,
          group: option.group,
        }))}
        sx={{
          flex: 1,
          [`.${inputBaseClasses.root}.${inputClasses.root}`]: {
            marginTop: 1,
          },
        }}
        TextFieldProps={{
          disabled,
          name: 'tagKey',
          placeholder,
          inputRef: keyInputRef,
        }}
        onChange={(_e, value, reason) => {
          if (value?.value !== 'tagKeyError') {
            onValueChange(value, true, reason);
          }
        }}
        onFocus={onKeyInputFocus}
        onInputChange={(_e, value) => onKeyInputChange(value.trim())}
        onKeyDown={onKeyInputKeydown}
        openOnFocus
        value={selectedTagKeyOption}
      />
      {valueInputVisible && (
        <>
          <Typography
            component='span'
            fontSize='1.5rem'
            mx={1.25}
            maxWidth='fit-content'
          >
            :
          </Typography>
          <Autocomplete
            autoHighlight
            createable={isValueFreeform && !valueInputError}
            createNewOption={(value) => ({ label: value, value })}
            createNewOptionText={valueInputErrorHint || null}
            disableCreateOption={
              !isValueFreeform || !valueEntered || valueInputError
            }
            filterOptions={(options, params) => {
              const filtered = tagValueFilter(options, params);

              const isExisting = options.some(
                ({ label, value }) =>
                  value === tagValueInput || label === tagValueInput
              );

              if (tagValueInput !== '' && !isExisting && !valueInputError) {
                filtered.push({
                  label: tagValueInput,
                  value: tagValueInput,
                });
              }

              if (valueInputErrorHint) {
                filtered.push({
                  label: valueInputErrorHint,
                  value: 'tagValueError',
                });
              }

              return filtered;
            }}
            fullWidth={fullWidth}
            loading={loading}
            options={[
              ...(showAnyValueSchemaOption
                ? [{ label: ANY_TAG_VALUE.label, value: ANY_TAG_VALUE.value }]
                : []),
              ...tagValueOptions.map((option) => ({
                label: getValueDropdownLabel(option),
                value: option.value,
              })),
            ]}
            sx={{
              flex: 1,
              [`.${inputBaseClasses.root}.${inputClasses.root}`]: {
                marginTop: 1,
              },
            }}
            TextFieldProps={{
              disabled,
              name: 'tagValue',
              inputRef: valueInputRef,
            }}
            onBlur={onValueInputBlur}
            onChange={(_e, value, reason) => {
              if (value?.value !== 'tagValueError') {
                onValueChange(value, false, reason);
              }
            }}
            onInputChange={(_e, value) => onValueInputChange(value.trim())}
            onKeyDown={onKeyInputKeydown}
            openOnFocus
            value={selectedTagValueOption}
          />
        </>
      )}
    </Box>
  );
};
